// src/components/AIhelper.js
import React, { useState, useMemo, useContext } from "react";
import { Card, Row, Col, Form, ButtonGroup, Button } from "react-bootstrap";
import { AIHelperContext } from "./AIHelperContext";
import {Context} from "../../index";
import {$host} from "../../http";


const AIhelper = ({ title = "", subtitle = "", genre = "", description = "", author = "" }) => {
    // Локальный режим — чтобы не ломать поведение
    const [mode, setMode] = useState("translator"); // "writer" | "translator"
    const isWriter = mode === "writer";
    const isTranslator = mode === "translator";
    const { user } = useContext(Context);

    const [inputText, setInputText] = useState("");
    const [outputText, setOutputText] = useState("");

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const reviewerName =
        (typeof user !== "undefined" && user?.user?.amz_name) ? user.user.amz_name : "Reader";

    const personaPrompt = [
        "Persona & style:",
        `• Imagine you are an ordinary book lover and everyday reader named "${reviewerName}".`,
        `• You just finished reading the book titled "${title || "Untitled"}"${subtitle ? ` with subtitle "${subtitle}"` : ""}, you liked it, and you're writing a short, positive, enthusiastic review.`,
        author ? `• You appreciate the author "${author}" for providing valuable insights.` : null,
        genre ? `• You generally enjoy books in the "${genre}" genre.` : null,
        "• If the book description mentions the names of HowItWorks characters, be sure to include them in the review.",
        "• Keep it natural, friendly, and believable. No spoilers.",

    ].filter(Boolean).join("\n");

    // Шаблонная часть промпта из метаданных книги
    const baseMetaBlock = useMemo(() => {
        const safe = (v) => (v || "").toString().trim();
        const t = safe(title) || "Untitled";
        const st = safe(subtitle);
        const g = safe(genre) || "General";
        const d = safe(description);
        const a = safe(author);

        return [
            "You are an assistant working on a book entry.",
            "Please produce clear, concise, high-quality text. Avoid hallucinations.",
            "",
            "Book metadata:",
            `• Title: ${t}`,
            st ? `• Subtitle: ${st}` : null,
            `• Genre: ${g}`,
            a ? `• Author: ${a}` : null,
            d ? `• Short Description (first lines): ${d}` : null,
        ]
            .filter(Boolean)
            .join("\n");
    }, [title, subtitle, genre, description, author]);


    // Нажатие Start — формируем конечный prompt и кладём в контекст
    const onStart = async () => {
        setError("");
        setLoading(true);

        try {
            // Base prompt (metadata + persona if you use it)
            let finalPrompt = [baseMetaBlock, "", personaPrompt].filter(Boolean).join("\n");

            if (mode === "writer") {
                // AI Writer: 2–3 reader-style review suggestions
                finalPrompt +=
                    "\n\nTask (Writer): Provide 3 short reader-style review suggestions (3–5 short sentences each). " +
                    "Tone: friendly, natural, believable. No spoilers." +
                    "Use simple, everyday American teen language — casual and clear." +
                    "Avoid rare or complex words, and keep emotions natural, not exaggerated." +
                    "• Format the output as:\n" +
                    "   Review Title: <short title, 3–6 words>\n" +
                    "   Review Text: <the full translated review body>";
            } else {
                // AI Translator: always rewrite in English + add relevant metadata details
                const rawInput = (inputText || "").trim();

                // word-limit: same length or at most 1.5x the user's input
                const inputWordCount = rawInput ? rawInput.split(/\s+/).filter(Boolean).length : 0;
                const maxWords = inputWordCount ? Math.ceil(inputWordCount * 1.5) : 180; // fallback cap

                const translatorInstructions =
                    "Translator task:\n" +
                    "• Detect the INPUT language and rewrite it in English.\n" +
                    "• Keep the user's meaning, sentiment, and voice; improve clarity, grammar, and flow.\n" +
                    "• Add relevant details from metadata if helpful:\n" +
                    (author ? `  – Mention the author "${author}" naturally.\n` : "") +
                    "  – If HowItWorks character names are recognized in the Description, include them naturally.\n" +
                    "  – Reflect key themes emphasized in the Description when appropriate.\n" +
                    "• Do not invent facts beyond the INPUT and metadata.\n" +
                    "• No spoilers.\n" +
                    "• First-person reviewer tone.\n" +
                    `• Length: keep about the same as INPUT; MUST NOT exceed ${maxWords} words (≈ 1.5× of the input).\n` +
                    "• Output the final text only (no preface, no notes).\n" +
                    "• Use casual, everyday American teen language — simple, natural, and relatable.\n" +
                    "• Avoid rare or complicated words, and don’t exaggerate emotions.\n" +
                    "• Format the output as:\n" +
                    "   Review Title: <short title, 3–6 words>\n" +
                    "   Review Text: <the full translated review body>";


                finalPrompt += "\n\n" + translatorInstructions;

                if (rawInput) {
                    finalPrompt += "\n\n=== INPUT BEGIN ===\n" + rawInput + "\n=== INPUT END ===";
                }
            }

            const messages = [
                { role: "system", content: "You are a helpful assistant for an author platform. Be concise and accurate." },
                { role: "user", content: finalPrompt }
            ];

            const { data } = await $host.post("api/ai/chat-completions", {
                model: "openai/gpt-4o-mini-2024-07-18",
                messages,
                temperature: 0.7,
            });

            const content = data?.choices?.[0]?.message?.content || "";
            setOutputText(content);
        } catch (e) {
            console.error(e);
            setError(e.message || "OpenRouter request failed");
        } finally {
            setLoading(false);
        }
    };
    const startDisabled =
        loading ||
        (mode === "translator" && inputText.trim().length < 100);

    const startLabel = loading
        ? "Working..."
        : isWriter
            ? "Generate Suggestions"
            : (inputText.trim().length < 100
                ? "Enter 100 characters to Start"
                : "Translate & Refine");




    return (
        <Card className="w-100 mt-0 mb-2 border shadow-sm border-success position-relative">
            <Card.Body className="p-2 mt-2 pb-5"> {/* pb-5 чтобы контент не упирался в кнопку */}
            <div className="d-flex flex-column w-100 gap-2">
                {/* Заголовок + кнопки в одной строке */}
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 w-100">
                    <h5 className="text-muted mb-0">
                        {mode === "writer" ? "Generate Review Suggestions from Metadata" : "Write Review in your language. We’ll translate, refine, and format."}
                    </h5>

                    <div className="d-flex align-items-center gap-2">
                        <ButtonGroup>
                            <Button
                                variant={isWriter ? "success" : "outline-success"}
                                size="sm"
                                onClick={() => {
                                    setMode("writer");
                                    setOutputText("");
                                }}
                            >
                               Suggestions Generator
                            </Button>
                              <Button
                                variant={isTranslator ? "success" : "outline-success"}
                                size="sm"
                                onClick={() => {
                                    setMode("translator");
                                    setOutputText("");
                                }}
                            >
                                Review Translator
                            </Button>
                        </ButtonGroup>



                    </div>
                </div>

                {/* Описание под заголовком */}
                <p className=" text-muted mb-0">
                    {mode === "writer" ? (
                        <>
                            Generate potential reader-style reviews from your book’s metadata.
                            If one matches what you want to say, just edit it and use it.
                            Reviews are short, casual, and sound like everyday American teens.
                        </>
                    ) : (
                        <>

                                Write your review in your native language — English, Spanish, German, Italian, Ukrainian, Russian, French, or Portuguese.
                                The system will automatically rewrite it in English, keeping your original meaning and tone,
                                while adding key details such as the author’s name, main characters, or genre when relevant.
                                The final text will stay casual, clear, and natural — just like a friendly reader’s review.


                        </>
                    )}
                </p>
            </div>

            {/* Body */}
            {isWriter ? (
                // Writer: только Output на всю ширину
                <Row className="g-3 mt-2 mb-3">
                    <Col xs={12}>
                        <Card className="h-100 border border-secondary">
                            <Card.Header className="py-1 fw-semibold">Generate possible reviews from the metadata. If the text already conveys your thoughts, edit it lightly and reuse.</Card.Header>
                            <Card.Body className="p-2">
                                <Form.Control
                                    as="textarea"
                                    rows={10}
                                    placeholder={`Generate reviews from book metadata. If they fit your idea — edit and use them.
Genere reseñas a partir de metadatos. Si expresan su idea — edítelas y utilícelas.
Сгенерируйте отзывы по метаданным книги. Если они отражают вашу мысль — используйте.
Erstellen Sie Rezensionen aus den Metadaten. Wenn sie Ihre Idee wiedergeben — nutzen Sie sie.
Genera recensioni dai metadati del libro. Se trasmettono la tua idea — modificale e usale.
Générez des avis à partir des métadonnées. S’ils expriment votre idée — modifiez-les.
Gere resenhas com base nos metadados. Se refletirem sua ideia — edite e use-as.`}



                                    value={outputText}
                                    onChange={(e) => setOutputText(e.target.value)}
                                    className="form-control-sm small"
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            ) : (
                // Translator: две колонки Input + Output
                <Row className="g-3 mt-2 mb-3">
                    <Col xs={12} md={6}>
                        <Card className="h-100 border border-secondary">
                            <Card.Header className="py-1 d-flex justify-content-between align-items-center fw-semibold">
                                <span>Write your review in your native language</span>
                                <small className={inputText.trim().length < 100 ? "text-danger" : "text-muted"}>
                                    {inputText.trim().length} / 100 chars
                                </small>
                            </Card.Header>
                            <Card.Body className="p-2">
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    placeholder={`Write your review in your native language (min 100 characters)...
Escribe tu reseña en español (mínimo 100 caracteres)...
Schreiben Sie Ihre Rezension auf Deutsch (mindestens 100 Zeichen)...
Scrivi la tua recensione in italiano (almeno 100 caratteri)...
Напишіть свій відгук українською мовою (мінімум 100 символів)...
Напишите ваш отзыв на русском языке (минимум 100 символов)...
Écrivez votre avis en français (minimum 100 caractères)...
Escreva sua avaliação em português (mínimo 100 caracteres)...
`}

                                    value={inputText}
                                    onChange={(e) => setInputText(e.target.value)}
                                    className="form-control-sm small"
                                />
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col xs={12} md={6}>
                        <Card className="h-100 border border-secondary">
                            <Card.Header className="py-1 fw-semibold">Translated and enriched English text will appear here…</Card.Header>
                            <Card.Body className="p-2">
                                <Form.Control
                                    as="textarea"
                                    rows={5}
                                    placeholder={`Translated and enriched English text will appear here…
El texto en inglés traducido y enriquecido aparecerá aquí…
Der übersetzte und ergänzte englische Text erscheint hier…
Il testo inglese tradotto e arricchito apparirà qui…
Перекладений і доповнений англійський текст з’явиться тут…
Переведённый и дополненный английский текст появится здесь…
Le texte anglais traduit et enrichi apparaîtra ici…
O texto em inglês traduzido e enriquecido aparecerá aqui…`}


                                    value={outputText}
                                    onChange={(e) => setOutputText(e.target.value)}
                                    className="form-control-sm small"
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
                <Button
                    variant="success"
                    size="sm"
                    onClick={onStart}
                    disabled={startDisabled}
                    className="position-absolute bottom-0 end-0 m-3 me-4"
                >
                    {startLabel}
                </Button>
            {error && <div className="mt-2 small text-danger">{error}</div>}
            </Card.Body>
        </Card>
    );
};

export default AIhelper;
